import React from 'react';
import { Spinner } from '../Spinner/Spinner';
import type { ButtonBaseProps, ButtonSize } from './types';

export type ButtonVariant = 'primary' | 'secondary';
export type { ButtonSize };

export const SIZE_CLASSES: Record<ButtonSize, string> = {
    md: 'h-9 px-l gap-s',
    sm: 'h-7 px-m gap-1.5',
};

/** Chrome shared by every boxed, bordered control built on this shape (Button itself, and the
 * tone-outlined action buttons in Banner/ToneSelector) — shape, elevation and focus ring, with
 * colour and layout left to the caller. */
export const BUTTON_BASE_CHROME =
    'rounded-lg whitespace-nowrap shadow-weak transition-colors duration-200 touch-target ' +
    'focus-visible:outline-none focus-visible:border-focus focus-visible:ring-4 focus-visible:ring-focus-ring';

/**
 * Hover/active intentionally deepen in both themes rather than following the ramp's
 * light/dark mirror — a mirrored value would get lighter as you press in dark mode,
 * which reads as inverted feedback. That's why each state pins an explicit `dark:`
 * step instead of relying on the usual no-dark:-needed mirroring.
 */
const VARIANT_ENABLED: Record<ButtonVariant, string> = {
    primary:
        'bg-primary-900 hover:bg-primary-1200 active:bg-primary-1250 ' +
        'dark:bg-primary-1250 dark:hover:bg-primary-1000 dark:active:bg-primary-900 ' +
        'text-content-on-primary font-semibold',
    secondary:
        'bg-primary-50 hover:bg-primary-200 active:bg-primary-300 ' +
        'dark:bg-primary-100 dark:hover:bg-primary-400 dark:active:bg-primary-500 ' +
        'border border-primary-600 text-content font-semibold',
};

/** Disabled keeps each variant's shape (filled vs. bordered) and weight rather than collapsing both to one look. */
const VARIANT_DISABLED: Record<ButtonVariant, string> = {
    primary: 'bg-primary-600 text-content-on-primary-disabled border border-transparent font-semibold',
    secondary: 'bg-primary-50 dark:bg-primary-100 text-content-tertiary border border-line-subtle font-semibold',
};

export const buttonClassName = (variant: ButtonVariant = 'primary', disabled = false, size: ButtonSize = 'md'): string => {
    const base =
        `${SIZE_CLASSES[size]} ${BUTTON_BASE_CHROME} text-compact inline-flex items-center justify-center focus-visible:border`;
    const colors = disabled
        ? `${VARIANT_DISABLED[variant]} cursor-not-allowed`
        : `${VARIANT_ENABLED[variant]} cursor-pointer`;
    return `${base} ${colors}`;
};

export const Button: React.FC<ButtonBaseProps<ButtonVariant>> = ({
    className = '',
    children,
    disabled,
    loading = false,
    variant = 'primary',
    size = 'md',
    ...props
}) => {
    const isDisabled = disabled || loading;

    return (
        <button
            className={`${buttonClassName(variant, isDisabled, size)} ${className}`}
            disabled={isDisabled}
            aria-busy={loading}
            {...props}
        >
            {loading && <Spinner className="size-text" />}
            {children}
        </button>
    );
};
